import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import AppHeader from '../../components/AppHeader/AppHeader';
import ApiService from '../../services/ApiService';
import AppTooltip from '../../components/AppTooltip/AppTooltip';
import Pagination from '../../components/Pagination';
import { AUDIT_TYPE_OPTIONS } from '../../constants/audit';
import { useAuth } from '../../contexts/AuthContext';
import { formatNameByCountry } from '../../utils/nameFormatter';
import loadingGif from '../../../assets/loading.gif';
import '../../styles/style.css';
import './Audit.css';

interface AuditRecord {
  id: number;
  service_method: string;
  http_verb: string;
  audit_type: string;
  user_id: number;
  message: string;
  state: string;
  ref_id: string | null;
  ref_type: string | null;
  date: string;
}

interface AuditResponse {
  is_success: boolean;
  status: number;
  message: string;
  data: Array<{
    audits: AuditRecord[];
    total_count: number;
    page: number;
    page_size: number;
    total_pages: number;
  }>;
}

interface FilterParams {
  audit_type?: string;
  start_date?: string;
  end_date?: string;
  user_id?: string;
}

const Audit: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const currentUserId = user ? String(user.id ?? (user as any).user_id ?? (user as any).ID ?? '') : '';

  const [auditData, setAuditData] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(8);
  const [pagination, setPagination] = useState({
    total_count: 0,
    page: 1,
    page_size: 8,
    total_pages: 1,
  });

  const [filters, setFilters] = useState<FilterParams>({
    audit_type: '',
    start_date: '',
    end_date: '',
    user_id: isAdmin ? '' : currentUserId,
  });
  const [usersById, setUsersById] = useState<Record<number, string>>({});
  const [userOptions, setUserOptions] = useState<{ id: number; displayName: string }[]>([]);
  const [pageSizeInitialized, setPageSizeInitialized] = useState<boolean>(false);

  const apiService = ApiService.getInstance();
  useEffect(() => {
    let isMounted = true;

    const loadUsers = async () => {
      try {
        const baseUrl = apiService.getAuthServiceUrl();
        const response = await apiService.get<any>('/grader/get-users', undefined, baseUrl);
        if (!isMounted) {
          return;
        }

        if (response.success && response.data) {
          const responseData = response.data as any;
          const rawData = Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData)
              ? responseData
              : [];

          let usersList: any[] = [];

          if (Array.isArray(rawData)) {
            rawData.forEach(item => {
              if (Array.isArray(item?.users)) {
                usersList = usersList.concat(item.users);
              } else if (item) {
                usersList.push(item);
              }
            });
          } else if (Array.isArray(responseData?.users)) {
            usersList = responseData.users;
          }

          const mappedUsers = usersList
            .map(userEntry => {
              const firstName = userEntry?.first_name ?? userEntry?.firstName ?? '';
              const lastName = userEntry?.last_name ?? userEntry?.lastName ?? '';
              const middleName = userEntry?.middle_name ?? userEntry?.middleName ?? '';
              const formattedName = formatNameByCountry(firstName, lastName, middleName, 'IND');
              const fallback =
                formattedName && formattedName !== '-'
                  ? formattedName
                  : (userEntry?.username || userEntry?.email || userEntry?.name || '').trim();

              return {
                id: Number(userEntry?.id ?? userEntry?.user_id ?? userEntry?.ID ?? 0),
                displayName: fallback || `User ${userEntry?.id ?? userEntry?.user_id ?? ''}`,
              };
            })
            .filter(userEntry => userEntry.id > 0);

          const map = mappedUsers.reduce((acc, userItem) => {
            acc[userItem.id] = userItem.displayName;
            return acc;
          }, {} as Record<number, string>);

          setUsersById(map);
          setUserOptions(mappedUsers);
        }
      } catch (err) {
        console.warn('Failed to fetch users for audit logs:', err);
      }
    };

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [apiService]);

  useEffect(() => {
    if (!isAdmin && currentUserId) {
      setFilters(prev => {
        if (prev.user_id === currentUserId) {
          return prev;
        }
        return { ...prev, user_id: currentUserId };
      });
    }
  }, [isAdmin, currentUserId]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    const desiredSize = 8; // Always use page size 8 for all users
    if (pageSize !== desiredSize) {
      setPageSize(desiredSize);
      setCurrentPage(1);
    }
    setPageSizeInitialized(true);
  }, [authLoading, pageSize]);

  const buildQueryParams = (): Record<string, any> => {
    const queryParams: Record<string, any> = {
      page: currentPage,
      size: pageSize,
    };

    // Add filter params only if they have values
    if (filters.audit_type) queryParams.audit_type = filters.audit_type;
    if (filters.start_date) queryParams.start_date = filters.start_date;
    if (filters.end_date) queryParams.end_date = filters.end_date;
    if (filters.user_id) queryParams.user_id = filters.user_id;

    return queryParams;
  };

  const fetchAuditData = async () => {
    try {
      setLoading(true);
      setError(null);

      const baseUrl = apiService.getUserManagementUrl();
      const queryParams = buildQueryParams();

      const response = await apiService.get<AuditResponse>('/audit', queryParams, baseUrl);

      if (response.success && response.data) {
        const auditResponse = response.data as AuditResponse;
        if (auditResponse.is_success && auditResponse.data && auditResponse.data.length > 0) {
          setAuditData(auditResponse.data[0].audits || []);
          setPagination({
            total_count: auditResponse.data[0].total_count || 0,
            page: auditResponse.data[0].page || 1,
            page_size: auditResponse.data[0].page_size || 8,
            total_pages: auditResponse.data[0].total_pages || 1,
          });
          setCurrentPage(auditResponse.data[0].page || 1);
        } else {
          setAuditData([]);
        }
      } else {
        setError(response.error || 'Failed to fetch audit data');
        setAuditData([]);
      }
    } catch (err) {
      console.error('Error fetching audit data:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setAuditData([]);
    } finally {
      setLoading(false);
    }
  };

  // Call API when page or pageSize changes
  useEffect(() => {
    if (authLoading || !pageSizeInitialized) {
      return;
    }
    fetchAuditData();
  }, [currentPage, pageSize, authLoading, pageSizeInitialized]);

  // Call API when filters change (except page and size which are handled separately)
  useEffect(() => {
    if (authLoading || !pageSizeInitialized) {
      return;
    }
    if (currentPage === 1) {
      fetchAuditData();
    } else {
      setCurrentPage(1);
    }
  }, [filters.audit_type, filters.start_date, filters.end_date, filters.user_id, authLoading, pageSizeInitialized]);

  const handleFilterChange = (key: keyof FilterParams, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    if (authLoading || !pageSizeInitialized) {
      return;
    }
    setCurrentPage(1);
    fetchAuditData();
  };

  const handleClearFilters = async () => {
    const clearedFilters = {
      audit_type: '',
      start_date: '',
      end_date: '',
      user_id: isAdmin ? '' : currentUserId,
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    const newPageSize = 8; // Always use page size 8 for all users
    setPageSize(newPageSize);

    // Immediately call API with cleared filters
    try {
      setLoading(true);
      setError(null);

      const baseUrl = apiService.getUserManagementUrl();
      const queryParams: Record<string, any> = {
        page: 1,
        size: newPageSize,
      };

      // Only add user_id if it's not empty (for non-admin users)
      if (clearedFilters.user_id) {
        queryParams.user_id = clearedFilters.user_id;
      }

      const response = await apiService.get<AuditResponse>('/audit', queryParams, baseUrl);

      if (response.success && response.data) {
        const auditResponse = response.data as AuditResponse;
        if (auditResponse.is_success && auditResponse.data && auditResponse.data.length > 0) {
          setAuditData(auditResponse.data[0].audits || []);
          setPagination({
            total_count: auditResponse.data[0].total_count || 0,
            page: auditResponse.data[0].page || 1,
            page_size: auditResponse.data[0].page_size || newPageSize,
            total_pages: auditResponse.data[0].total_pages || 1,
          });
        } else {
          setAuditData([]);
        }
      } else {
        setError(response.error || 'Failed to fetch audit data');
        setAuditData([]);
      }
    } catch (err) {
      console.error('Error fetching audit data:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setAuditData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    // Page size is fixed at 8 for all users
    // This function is kept for compatibility but doesn't change page size
    return;
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return '-';
      return moment(dateString).format('DD MMM YYYY, hh:mm A');
    } catch (err) {
      return dateString;
    }
  };

  const getUserDisplayName = (userId?: number | null) => {
    if (!userId) {
      return '—';
    }
    return usersById[userId] || `User ${userId}`;
  };

  const sortedUserOptions = useMemo(() => {
    return [...userOptions].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [userOptions]);

  const getAuditTypeColor = (auditType: string) => {
    switch (auditType?.toUpperCase()) {
      case 'CREATE':
        return '#10b981'; // green
      case 'UPDATE':
        return '#3b82f6'; // blue
      case 'DELETE':
        return '#ef4444'; // red
      case 'ACCESS':
        return '#8b5cf6'; // purple
      case 'FAILED':
        return '#f59e0b'; // amber
      default:
        return '#6b7280'; // gray
    }
  };


  if (error) {
    return (
      <div
        className="app-dark-bg"
        style={{ minHeight: '100vh' }}
      >
        <AppHeader />
        <div style={{ flex: 1, padding: '20px', color: 'white' }}>
          <div
            style={{
              background: '#0b103a',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              padding: '16px',
              color: '#ef4444',
            }}
          >
            <h3 style={{ marginTop: 0 }}>Error Loading Audit Data</h3>
            <p>{error}</p>
            <button
              onClick={fetchAuditData}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-dark-bg"
      style={{ minHeight: '100vh' }}
    >
      <AppHeader />

      <div style={{ display: 'flex', flexDirection: 'column',  margin: '0.5rem 1.5rem' }}>
        {/* Search and Filter Section - Always visible */}
        <div className="audit-filter-section" style={{ padding: '30px 0px 0px 0px', marginBottom: '20px' }}>
          <div className="audit-filter-controls">
        <div
          style={{
            display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'flex-end',
              }}
            >
              <div className="audit-filter-item">
                <label>Audit Type</label>
                  <select
                    value={filters.audit_type || ''}
                    onChange={(e) => handleFilterChange('audit_type', e.target.value)}
                    className="audit-filter-select"
                    style={{ width: '150px' }}
                  >
                    <option value="">All</option>
                    {AUDIT_TYPE_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
              </div>

              <div className="audit-filter-item">
                <label>User</label>
                <select
                  value={filters.user_id || ''}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  className="audit-filter-select"
                  style={{ width: '200px' }}
                  disabled={!isAdmin || sortedUserOptions.length === 0}
                >
                  <option value="">All</option>
                  {sortedUserOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="audit-filter-item">
                <label>Start Date</label>
                <div style={{ position: 'relative', display: 'inline-block', width: '150px' }}>
                  <input
                    type="text"
                    value={filters.start_date ? moment(filters.start_date).format('DD MMM YYYY') : ''}
                    placeholder="dd MMM yyyy"
                    readOnly
                    onClick={() => {
                      const picker = document.getElementById('auditStartDatePicker');
                      if (picker && 'showPicker' in picker) {
                        (picker as any).showPicker();
                      }
                    }}
                    style={{
                      background: '#090c29',
                      color: 'white',
                      border: '1px solid #3a3f99',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      paddingRight: '35px',
                      fontSize: '13px',
                      width: '100%',
                      cursor: 'pointer',
                    }}
                  />
                  <span
                    onClick={() => {
                      const picker = document.getElementById('auditStartDatePicker');
                      if (picker && 'showPicker' in picker) {
                        (picker as any).showPicker();
                      }
                    }}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      color: '#9ca3af',
                      fontSize: '16px',
                    }}
                  >
                    📅
                  </span>
                  <input
                    id="auditStartDatePicker"
                    type="date"
                    value={filters.start_date || ''}
                    max={filters.end_date || ''}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      // If end_date exists and new start_date is after it, clear end_date
                      if (filters.end_date && newStartDate && newStartDate > filters.end_date) {
                        handleFilterChange('end_date', '');
                      }
                      handleFilterChange('start_date', newStartDate);
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              </div>

              <div className="audit-filter-item">
                <label>End Date</label>
                <div style={{ position: 'relative', display: 'inline-block', width: '150px' }}>
                  <input
                    type="text"
                    value={filters.end_date ? moment(filters.end_date).format('DD MMM YYYY') : ''}
                    placeholder="dd MMM yyyy"
                    readOnly
                    onClick={() => {
                      const picker = document.getElementById('auditEndDatePicker');
                      if (picker && 'showPicker' in picker) {
                        (picker as any).showPicker();
                      }
                    }}
            style={{
                      background: '#090c29',
              color: 'white',
                      border: '1px solid #3a3f99',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      paddingRight: '35px',
                      fontSize: '13px',
                      width: '100%',
                      cursor: 'pointer',
                    }}
                  />
                  <span
                    onClick={() => {
                      const picker = document.getElementById('auditEndDatePicker');
                      if (picker && 'showPicker' in picker) {
                        (picker as any).showPicker();
                      }
                    }}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      color: '#9ca3af',
                      fontSize: '16px',
                    }}
                  >
                    📅
                  </span>
                  <input
                    id="auditEndDatePicker"
                    type="date"
                    value={filters.end_date || ''}
                    min={filters.start_date || ''}
                    onChange={(e) => {
                      const newEndDate = e.target.value;
                      // If start_date exists and new end_date is before it, clear start_date
                      if (filters.start_date && newEndDate && newEndDate < filters.start_date) {
                        handleFilterChange('start_date', '');
                      }
                      handleFilterChange('end_date', newEndDate);
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="audit-search-buttons">
              <button
                className="audit-search-button"
                onClick={handleApplyFilters}
              >
                Search
              </button>
              <button
                className="audit-clear-button"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="audit-loading-container">
            <img src={loadingGif} alt="Loading..." className="audit-loading-gif" />
          </div>
        ) : (
          <>
            <table className="audit-table">
              <thead>
                <tr>
                  <th className="audit-sl-no-header" style={{ width: '80px' }}>Sl No</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>User</th>
                  <th>Message</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {auditData.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#cbd5e0' }}>
                      No Data Found
                    </td>
                  </tr>
                ) : (
                  auditData.map((record, index) => (
                    <tr key={record.id}>
                      <td className="audit-sl-no-header" style={{ width: '80px' }}>
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td>
                        <AppTooltip side="top" sideOffset={6} content={formatDate(record.date)}>
                          {formatDate(record.date)}
                        </AppTooltip>
                      </td>
                      <td>
                        <AppTooltip side="top" sideOffset={6} content={record.audit_type}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: getAuditTypeColor(record.audit_type),
                                display: 'inline-block',
                              }}
                            />
                            {record.audit_type}
                          </span>
                        </AppTooltip>
                      </td>
                      <td>
                        <AppTooltip side="top" sideOffset={6} content={getUserDisplayName(record.user_id)}>
                          {getUserDisplayName(record.user_id)}
                        </AppTooltip>
                      </td>
                      <td style={{ maxWidth: '300px', wordBreak: 'break-word', lineHeight: 1.4 }}>
                        <AppTooltip side="top" sideOffset={6} content={record.message || 'No message'}>
                          {record.message || <span style={{ color: '#6b7280' }}>—</span>}
                        </AppTooltip>
                      </td>
                      <td style={{ maxWidth: '220px', wordBreak: 'break-word', lineHeight: 1.4 }}>
                        <AppTooltip
                          side="top"
                          sideOffset={6}
                          content={record.ref_type && record.ref_id ? `${record.ref_type}: ${record.ref_id}` : 'No reference'}
                        >
                          {record.ref_type && record.ref_id ? (
                            <span>
                              {record.ref_type}: {record.ref_id}
                            </span>
                          ) : (
                            <span style={{ color: '#6b7280' }}>—</span>
                          )}
                        </AppTooltip>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {auditData.length > 0 && (
              <div style={{ paddingTop: '10px' }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination.total_pages || 1}
                  totalCount={pagination.total_count || 0}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Audit;
